import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { useSearchParams } from 'react-router-dom'
import { ImagePreviewWrapperSizeEnum } from 'shared/ui/ImagePreviewWrapper/ImagePreviewWrapper'

import { AppTableMobile } from 'components/AppTable/AppTableMobile'
import { IChooseItems } from 'core/api/dto/BuffetDishDto'
import { AppTableImageViewer } from 'components/AppTableImageViewer/AppTableImageViewer'
import { useDeleteAiDish } from '../hooks/useDeleteAiDish'
import DishAiImagePopup from './DishAiImagePopup'
import { mapComplianceToLabel, mapSourceToLabel } from 'utils/complianceHelpers'
import { AppDeletePopup } from 'entities/AppDeletePopup/AppDeletePopup'
import moment from 'moment'
import { useGetStandardAiDatasetListQuery } from '../../../core/api/StandardAiDataset'
import { IStandardAiDatasetDto } from 'core/api/dto/StandardAiDatasetDto'
import { useGetMismatchTypeListQuery } from 'core/api/TypeMismatchApi'
import { AppLoader, LoaderType } from 'components/AppLoader'
import { AiDatasetStatsRow } from './AiDatasetStatsRow'
import { useGetBuffetDishDetailQuery } from 'core/api/BuffetDishApi'

const MIN_MATCH_PHOTOS_FOR_TRAINING = 50

const MEDIA_HOST = process.env.REACT_APP_API_PROTOCOL
  ? `${process.env.REACT_APP_API_PROTOCOL}://${process.env.REACT_APP_API_URL}`
  : ''

const toAbsoluteUrl = (url: string) =>
  url.startsWith('/') ? `${MEDIA_HOST}${url}` : url

export enum DetailTab {
  'main' = 'main',
  'info' = 'info',
}

export const InfoTab = () => {
  const { dishId } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()

  const [file, setFile] = useState<null | File>(null)
  const [filePreview, setFilePreview] = useState<null | string>(null)
  const limit = Number(searchParams.get('limit')) || 40

  const queryParams = useMemo(() => {
    const defaultOrdering = '-created_at'
    const ordering = searchParams.get('ordering') || defaultOrdering
    return {
      ordering,
      dish_id: dishId,
      is_archived: false,
      limit,
      uploaded_from: searchParams.get('uploaded_from') || undefined,
      compliance_with_standards:
        searchParams.get('compliance_with_standards') || undefined,
      non_compliant_type_ids:
        searchParams.get('non_compliant_type_ids')?.split(',') || undefined,
    }
  }, [searchParams])

  const {
    data: standardDatasets,
    isLoading,
    isFetching,
  } = useGetStandardAiDatasetListQuery(queryParams)

  const { data: dishDetail } = useGetBuffetDishDetailQuery(
    { id: Number(dishId) },
    { skip: !dishId },
  )

  const [dishImagePopup, setDishImagePopup] = useState<{ active: boolean }>({
    active: false,
  })

  const { data: mismatchTypeList } = useGetMismatchTypeListQuery({
    is_archived: false,
  })

  const mismatchOptions = useMemo(
    () =>
      mismatchTypeList?.results
        ? mismatchTypeList.results.map((r) => ({ id: r.id, name: r.name }))
        : [],
    [mismatchTypeList],
  )

  const currentImageId = useMemo(() => {
    const param = searchParams.get('id')
    return param ? Number(param) : null
  }, [searchParams])

  useEffect(() => {
    if (currentImageId !== null && !dishImagePopup.active) {
      setDishImagePopup({ active: true })
    } else if (currentImageId === null && dishImagePopup.active) {
      setDishImagePopup({ active: false })
    }
  }, [currentImageId])

  const openDishImagePopup = (
    photo: string,
    name?: string,
    imageId?: number,
  ) => {
    if (imageId !== undefined && imageId !== null) {
      const newParams = new URLSearchParams(searchParams)
      newParams.set('id', String(imageId))
      setSearchParams(newParams, { replace: true })
    }
    setDishImagePopup({ active: true })
  }

  const loadMoreDatasets = () => {
    const newParams = new URLSearchParams(searchParams)
    newParams.set('limit', String(limit + 40))
    setSearchParams(newParams, { replace: true })
  }

  const closeDishImagePopup = () => {
    const newParams = new URLSearchParams(searchParams)
    newParams.delete('id')
    newParams.delete('mismatchType')
    setSearchParams(newParams, { replace: true })
    setDishImagePopup({ active: false })
  }

  useEffect(() => {
    if (!!file) {
      setFilePreview(URL.createObjectURL(file))
    }
  }, [file])

  const openImagePopupRowClick = (item: IStandardAiDatasetDto) => () =>
    openDishImagePopup(
      item.imagePreview || item.imageOnS3 || '',
      item.dish?.name || '',
      item.id,
    )

  const imagesTableDataSelectors = [
    {
      renderItem: (item: IStandardAiDatasetDto) => (
        <AppTableImageViewer
          imgPreview={item.imagePreviewOnS3 || item.imagePreview}
          image={item.imagePreview || item.imageOnS3 || ''}
          imageName={item.dish?.name || ''}
          archived={item.isArchived}
          size={ImagePreviewWrapperSizeEnum.MEDIUM}
          onImageClick={(photo, title) =>
            openDishImagePopup(photo, title, item.id)
          }
        />
      ),
    },
    {
      renderItem: (item: IStandardAiDatasetDto) => (
        <div
          className="dish-ai-row-clickable"
          onClick={openImagePopupRowClick(item)}
        >
          {mapSourceToLabel(item.uploadedFrom) || '-'}
        </div>
      ),
    },
    {
      renderItem: (item: IStandardAiDatasetDto) => {
        const { label, className } = mapComplianceToLabel(
          item.complianceWithStandards,
        )
        return (
          <div
            className={`table-status ${className} dish-ai-row-clickable`}
            onClick={openImagePopupRowClick(item)}
          >
            {label}
          </div>
        )
      },
    },
    {
      renderItem: (item: IStandardAiDatasetDto) => {
        const complianceStatus = item.complianceWithStandards
        return (
          <div
            className="dish-ai-row-clickable"
            onClick={openImagePopupRowClick(item)}
          >
            {complianceStatus === 'Not_determined'
              ? '-'
              : item.nonCompliantTypes?.join(', ').toLocaleLowerCase() || ''}
          </div>
        )
      },
    },
    {
      renderItem: (item: IStandardAiDatasetDto) => (
        <div
          className="dish-ai-row-clickable"
          onClick={openImagePopupRowClick(item)}
        >
          {moment.parseZone(item.createdAt).format('DD.MM.YYYY, HH:mm')}
        </div>
      ),
    },
    {
      renderItem: (item: IStandardAiDatasetDto) => {
        const link = item.imageOnS3 || item.image
        if (!link) return <div>-</div>
        return (
          <a
            href={toAbsoluteUrl(link)}
            target="_blank"
            rel="noopener noreferrer"
            className="dish-ai-image-link"
          >
            Открыть
          </a>
        )
      },
    },
  ]

  const headerData = [
    { title: 'Фото', colWidth: '5%' },
    { title: 'Источник', colWidth: '17%', sort: 'uploaded_from' },
    { title: 'Статус', colWidth: '15%', sort: 'compliance_with_standards' },
    {
      title: 'Тип несоответствия',
      colWidth: '20%',
      sort: 'non_compliant_types',
    },
    { title: 'Дата и время', colWidth: '15%', sort: 'created_at' },
    { title: 'Ссылка', colWidth: '8%' },
  ]

  const [chooseItems, setChooseItems] = useState<IChooseItems | null>({})
  const [tableData, setTableData] = useState([])

  const {
    deletePopup,
    onDeletePopupHandler,
    onDeleteHandler,
    closeDeletePopup,
  } = useDeleteAiDish(chooseItems, setChooseItems)

  const tableActions = [
    {
      title: 'Удалить',
      ico: 'basket',
      onClick: onDeletePopupHandler,
    },
  ]

  if (isLoading) {
    return <AppLoader loaderType={LoaderType.aiTable} />
  }

  if (!standardDatasets) return null

  const complianceDetails = standardDatasets.complianceWithStandardsDetails
  const matchCount = complianceDetails?.compliant || 0
  const readyForTraining =
    matchCount >= MIN_MATCH_PHOTOS_FOR_TRAINING && !dishDetail?.isArchived

  return (
    <>
      <div className="tab-content">
        <AiDatasetStatsRow
          total={complianceDetails?.amount || 0}
          matchCount={matchCount}
          noMatchCount={complianceDetails?.nonCompliant || 0}
          notLabeledCount={complianceDetails?.notDetermined || 0}
          readyForTraining={readyForTraining}
          mismatchOptions={mismatchOptions}
        />
        <AppTableMobile
          data={standardDatasets}
          headerData={headerData}
          chooseActions={tableActions}
          chooseMode
          chooseItems={chooseItems}
          setChooseItems={setChooseItems}
          setTableData={setTableData as any}
          idName={'id'}
          tableDataSelectors={imagesTableDataSelectors}
          tableClassName="dish-ai "
          isLoading={isFetching}
        />
      </div>
      {deletePopup && (
        <AppDeletePopup onClose={closeDeletePopup} onDelete={onDeleteHandler} />
      )}
      {dishImagePopup.active && standardDatasets && (
        <DishAiImagePopup
          active={dishImagePopup.active}
          onClose={closeDishImagePopup}
          reportImagePopupData={standardDatasets.results}
          mismatchOptions={mismatchOptions}
          hasMore={!!standardDatasets.next}
          isFetching={isFetching}
          onLoadMore={loadMoreDatasets}
        />
      )}
    </>
  )
}
